import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./LoginPage.css";

const UpdatePasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken, user, login } = useAuth();

  const [form, setForm] = useState({
    new_password: "",
    confirm_password: "",
  });

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const token =
    accessToken ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access") ||
    localStorage.getItem("token") ||
    "";

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
    }
  }, [token, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const parseJsonSafe = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const updateLocalUser = async () => {
    const nextUser = {
      ...(user || {}),
      requires_password_change: false,
      requiere_cambio_clave: false,
      must_change_password: false,
      change_password_required: false,
      temporary_password: false,
    };

    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");

      if (storedUser) {
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...storedUser,
            requires_password_change: false,
            requiere_cambio_clave: false,
            must_change_password: false,
            change_password_required: false,
            temporary_password: false,
          })
        );
      }
    } catch {}

    if (typeof login === "function") {
      const refresh =
        localStorage.getItem("refreshToken") ||
        localStorage.getItem("refresh") ||
        "";

      await login({
        access: token,
        refresh,
        user: nextUser,
      });
    }

    return nextUser;
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.new_password || !form.confirm_password) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    if (form.new_password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (form.new_password !== form.confirm_password) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    if (!token) {
      setError("No se encontró el token de autenticación.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/auth/update-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_password: form.new_password,
          confirm_password: form.confirm_password,
        }),
      });

      const data = await parseJsonSafe(response);

      if (!response.ok || data.ok === false) {
        setError(data.error || data.detail || "Error al actualizar la contraseña.");
        return;
      }

      const nextUser = await updateLocalUser();
      const role = nextUser?.role || user?.role;

      if (["super_admin", "admin", "staff"].includes(role)) {
        navigate("/admin-panel", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    } catch {
      setError("Error de conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        background: "linear-gradient(135deg, #f7faff 0%, #eef4fb 52%, #f8fbff 100%)",
      }}
    >
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          width: "100%",
          maxWidth: 430,
          p: { xs: "28px 20px", sm: "36px 34px" },
          borderRadius: "28px",
          background: "rgba(255, 255, 255, 0.92)",
          border: "1px solid rgba(167, 188, 216, 0.34)",
          boxShadow: "0 26px 70px rgba(16, 35, 61, 0.1)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Typography
          component="h1"
          sx={{
            color: "#10233d",
            fontSize: { xs: "1.65rem", sm: "1.95rem" },
            lineHeight: 1.1,
            fontWeight: 700,
            mb: 1,
            textAlign: "center",
          }}
        >
          Actualizar contraseña
        </Typography>

        <Typography
          sx={{
            color: "#647688",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            fontWeight: 400,
            mb: 3,
            textAlign: "center",
          }}
        >
          Ingresa una nueva contraseña para continuar.
        </Typography>

        {error && (
          <Typography
            sx={{
              color: "#b42318",
              background: "#fff4f2",
              border: "1px solid #f3cbc6",
              borderRadius: "16px",
              p: "12px 14px",
              fontSize: "0.93rem",
              lineHeight: 1.5,
              mb: 2,
            }}
          >
            {error}
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              color: "#1f3550",
              fontSize: "0.95rem",
              lineHeight: 1.35,
              fontWeight: 600,
              mb: 1,
            }}
          >
            Nueva contraseña
          </Typography>

          <TextField
            type={showPassword1 ? "text" : "password"}
            variant="outlined"
            fullWidth
            size="small"
            name="new_password"
            value={form.new_password}
            onChange={handleChange}
            autoComplete="new-password"
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 48,
                background: "#ffffff",
                borderRadius: "16px",
              },
              "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d2ddeb",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#a9c0df",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#0a3d91",
                boxShadow: "0 0 0 4px rgba(21, 151, 229, 0.08)",
              },
              "& input": {
                color: "#10233d",
              },
              "& input:-webkit-autofill": {
                WebkitTextFillColor: "#10233d",
                WebkitBoxShadow: "0 0 0px 1000px #ffffff inset",
                transition: "background-color 9999s ease-in-out 0s",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyOutlinedIcon sx={{ color: "#8b99ab", fontSize: "1.15rem" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword1((prev) => !prev)}
                    edge="end"
                    disabled={loading}
                    sx={{ color: "#64748b" }}
                  >
                    {showPassword1 ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              color: "#1f3550",
              fontSize: "0.95rem",
              lineHeight: 1.35,
              fontWeight: 600,
              mb: 1,
            }}
          >
            Confirmar contraseña
          </Typography>

          <TextField
            type={showPassword2 ? "text" : "password"}
            variant="outlined"
            fullWidth
            size="small"
            name="confirm_password"
            value={form.confirm_password}
            onChange={handleChange}
            autoComplete="new-password"
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 48,
                background: "#ffffff",
                borderRadius: "16px",
              },
              "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d2ddeb",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#a9c0df",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#0a3d91",
                boxShadow: "0 0 0 4px rgba(21, 151, 229, 0.08)",
              },
              "& input": {
                color: "#10233d",
              },
              "& input:-webkit-autofill": {
                WebkitTextFillColor: "#10233d",
                WebkitBoxShadow: "0 0 0px 1000px #ffffff inset",
                transition: "background-color 9999s ease-in-out 0s",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockResetRoundedIcon sx={{ color: "#8b99ab", fontSize: "1.15rem" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword2((prev) => !prev)}
                    edge="end"
                    disabled={loading}
                    sx={{ color: "#64748b" }}
                  >
                    {showPassword2 ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="small"
          disabled={loading}
          sx={{
            minHeight: 45,
            borderRadius: "16px",
            background: "linear-gradient(135deg, #1695e4 0%, #0a3d91 100%)",
            color: "#ffffff !important",
            textTransform: "none",
            fontSize: "0.96rem",
            fontWeight: 700,
            boxShadow: "0 16px 34px rgba(10, 61, 145, 0.2)",
            py: 1.2,
            "&:hover": {
              background: "linear-gradient(135deg, #1187cf 0%, #083578 100%)",
              boxShadow: "0 18px 38px rgba(10, 61, 145, 0.24)",
            },
            "&:disabled": {
              color: "#ffffff !important",
              background: "linear-gradient(135deg, rgba(22, 149, 228, 0.52), rgba(10, 61, 145, 0.52))",
            },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: "#ffffff" }} /> : "Guardar contraseña"}
        </Button>
      </Box>
    </Box>
  );
};

export default UpdatePasswordPage;